<template>
  <section-collapseable
    title="Deal Probability"
    collapseable
    editable
    @edit="$refs.leadDealProbabilityModal.open(record)"
  >
    <div class="probability_header">
      <div :class="['probability_gauge', sectionNumberClass]">
        <div class="probability_gauge--meter"></div>

        <div class="probability_gauge--lines one"></div>
        <div class="probability_gauge--lines two"></div>
        <div class="probability_gauge--lines three"></div>
        <div class="probability_gauge--lines four"></div>
        <div class="probability_gauge--lines five"></div>

        <div class="probability_gauge--tick"></div>
        <div class="probability_gauge--min_line"></div>
      </div>

      <p class="probability_note">The more of these factors that are present for a lead, the more likely you are to get
        the deal.</p>
    </div>

    <!-- Expanded Section -->
    <template v-slot:expanded>
      <p class="probability_line">
        Has Good Reason for Selling?
        <span :class="['probability_answer', probabilityClass(probabilityReasonForSelling)]">
        {{ probabilityText(probabilityReasonForSelling) }}
      </span>
      </p>
      <p class="probability_line">
        Is the house vacant?
        <span :class="['probability_answer', probabilityClass(probabilityHouseVacant)]">
        {{ probabilityText(probabilityHouseVacant) }}
      </span>
      </p>
      <p class="probability_line">
        Does the house need repairs?
        <span :class="['probability_answer', probabilityClass(probabilityNeedsRepairs)]">
        {{ probabilityText(probabilityNeedsRepairs) }}
      </span>
      </p>
      <p class="probability_line">
        Is there an urgency to close?
        <span :class="['probability_answer', probabilityClass(probabilityUrgency)]">
        {{ probabilityText(probabilityUrgency) }}
      </span>
      </p>
      <p class="probability_line">
        Is asking price less than the Zestimate?
        <span :class="['probability_answer', probabilityClass(probabilityLowerThanZestimate)]">
        {{ probabilityText(probabilityLowerThanZestimate) }}
      </span>
      </p>
      <p class="probability_line">
        Doesn't want to list with an agent?
        <span :class="['probability_answer', probabilityClass(probabilityNoAgent)]">
        {{ probabilityText(probabilityNoAgent) }}
      </span>
      </p>
    </template>

    <modal-container>
      <modal-crud-form
        ref="leadDealProbabilityModal"
        title="Lead Deal Probability"
        form-component="lead-deal-probability-form"
        :hideDeleteButton="true"
      ></modal-crud-form>
    </modal-container>
  </section-collapseable>
</template>

<script>
export default {
  name: "LeadDealProbability",
  computed: {
    record() {
      // If activeRecord is null then return necessary empty info
      return this.$store.state.activeRecord ? this.$store.state.activeRecord.record : {
        deal_probability_has_good_reason_for_selling: 0,
        deal_probability_house_vacant: 0,
        deal_probability_needs_repairs: 0,
        deal_probability_has_urgency: 0,
        deal_probability_less_then_zestimate: 0,
        deal_probability_not_list_with_agent: 0,
      };
    },
    probabilityReasonForSelling() {
      return this.record.deal_probability_has_good_reason_for_selling;
    },
    probabilityHouseVacant() {
      return this.record.deal_probability_house_vacant;
    },
    probabilityNeedsRepairs() {
      return this.record.deal_probability_needs_repairs;
    },
    probabilityUrgency() {
      return this.record.deal_probability_has_urgency;
    },
    probabilityLowerThanZestimate() {
      return this.record.deal_probability_less_then_zestimate;
    },
    probabilityNoAgent() {
      return this.record.deal_probability_not_list_with_agent;
    },
    sectionNumberClass() {
      let numberOfSections = 0;

      if (this.record.deal_probability_has_good_reason_for_selling) numberOfSections++;
      if (this.record.deal_probability_house_vacant) numberOfSections++;
      if (this.record.deal_probability_needs_repairs) numberOfSections++;
      if (this.record.deal_probability_has_urgency) numberOfSections++;
      if (this.record.deal_probability_less_then_zestimate) numberOfSections++;
      if (this.record.deal_probability_not_list_with_agent) numberOfSections++;

      switch (numberOfSections) {
        case 0:
          return "zeroSections";
        case 1:
          return "oneSections";
        case 2:
          return "twoSections";
        case 3:
          return "threeSections";
        case 4:
          return "fourSections";
        case 5:
          return "fiveSections";
        case 6:
          return "sixSections";
        default:
          return "zeroSections";
      }
    }
  },
  methods: {
    probabilityText(value) {
      if(value === null) return '--'
      return value ? 'Yes' : 'No'
    },
    probabilityClass(value) {
      if(value === null) return ''
      return value ? 'true' : 'false'
    }
  },
};
</script>

<style lang="scss" scoped>
@import "../../../styles/variables/variables";

.probability_header {
  display: flex;
}

.probability_note {
  font-size: 0.875rem;
  line-height: 1.2;
  color: $text-muted;
}

$gaugeWidth: 100px;
$tickThickness: 12px;
$tickOffset: 8deg;
$outsideBorder: 5px;

$firstSection: $grey-100;
$secondSection: $grey-100;
$thirdSection: $grey-100;
$fourthSection: $grey-100;
$fifthSection: $grey-100;
$sixthSection: $grey-100;

.probability_gauge {
  position: relative;
  flex-shrink: 0;
  height: calc($gaugeWidth / 2);
  width: $gaugeWidth;
  margin-right: 20px;
  padding: $outsideBorder $outsideBorder 0;
  overflow: hidden;

  &.zeroSections {
    .probability_gauge--meter:before {
      background: conic-gradient($fourthSection 0deg 30deg, $fifthSection 30deg 60deg, $sixthSection 60deg 90deg, white 90deg 270deg, $firstSection 270deg 300deg, $secondSection 300deg 330deg, $thirdSection 330deg 360deg);
    }

    .probability_gauge--tick {
      background-color: $danger;
      transform: rotate(0deg - $tickOffset);
    }
  }

  &.oneSections {
    $firstSection: $danger;

    .probability_gauge--meter:before {
      background: conic-gradient($fourthSection 0deg 30deg, $fifthSection 30deg 60deg, $sixthSection 60deg 90deg, white 90deg 270deg, $firstSection 270deg 300deg, $secondSection 300deg 330deg, $thirdSection 330deg 360deg);
    }

    .probability_gauge--tick {
      background-color: $danger;
      transform: rotate(30deg - $tickOffset);
    }
  }

  &.twoSections {
    $firstSection: $danger;
    $secondSection: $warning;

    .probability_gauge--meter:before {
      background: conic-gradient($fourthSection 0deg 30deg, $fifthSection 30deg 60deg, $sixthSection 60deg 90deg, white 90deg 270deg, $firstSection 270deg 300deg, $secondSection 300deg 330deg, $thirdSection 330deg 360deg);
    }

    .probability_gauge--tick {
      background-color: $warning;
      transform: rotate(60deg - $tickOffset);
    }
  }

  &.threeSections {
    $firstSection: $danger;
    $secondSection: $warning;
    $thirdSection: $warning;

    .probability_gauge--meter:before {
      background: conic-gradient($fourthSection 0deg 30deg, $fifthSection 30deg 60deg, $sixthSection 60deg 90deg, white 90deg 270deg, $firstSection 270deg 300deg, $secondSection 300deg 330deg, $thirdSection 330deg 360deg);
    }

    .probability_gauge--tick {
      background-color: $warning;
      transform: rotate(90deg - $tickOffset);
    }
  }

  &.fourSections {
    $firstSection: $danger;
    $secondSection: $warning;
    $thirdSection: $warning;
    $fourthSection: $success;

    .probability_gauge--meter:before {
      background: conic-gradient($fourthSection 0deg 30deg, $fifthSection 30deg 60deg, $sixthSection 60deg 90deg, white 90deg 270deg, $firstSection 270deg 300deg, $secondSection 300deg 330deg, $thirdSection 330deg 360deg);
    }

    .probability_gauge--tick {
      background-color: $success;
      transform: rotate(120deg - $tickOffset);
    }
  }

  &.fiveSections {
    $firstSection: $danger;
    $secondSection: $warning;
    $thirdSection: $warning;
    $fourthSection: $success;
    $fifthSection: $success;

    .probability_gauge--meter:before {
      background: conic-gradient($fourthSection 0deg 30deg, $fifthSection 30deg 60deg, $sixthSection 60deg 90deg, white 90deg 270deg, $firstSection 270deg 300deg, $secondSection 300deg 330deg, $thirdSection 330deg 360deg);
    }

    .probability_gauge--tick {
      background-color: $success;
      transform: rotate(150deg - $tickOffset);
    }
  }

  &.sixSections {
    $firstSection: $danger;
    $secondSection: $warning;
    $thirdSection: $warning;
    $fourthSection: $success;
    $fifthSection: $success;
    $sixthSection: $success;

    .probability_gauge--meter:before {
      background: conic-gradient($fourthSection 0deg 30deg, $fifthSection 30deg 60deg, $sixthSection 60deg 90deg, white 90deg 270deg, $firstSection 270deg 300deg, $secondSection 300deg 330deg, $thirdSection 330deg 360deg);
    }

    .probability_gauge--tick {
      background-color: $success;
      transform: rotate(180deg - $tickOffset);
    }
  }

  &--meter {
    position: relative;
    z-index: 10;
    display: inline-grid;
    width: $gaugeWidth - ($outsideBorder * 2);
    height: $gaugeWidth - ($outsideBorder * 2);
    aspect-ratio: 1;
    place-content: center;

    // Pie Chart
    &:before {
      // section colors designated above
      content: "";
      position: absolute;
      border-radius: 50%;
      inset: 0;
      border: 1px solid $white;
    }
  }

  &--lines {
    position: absolute;
    z-index: 12;
    bottom: 0;
    left: $outsideBorder;
    right: 50%;
    height: 1px;
    background-color: $white;
    transform-origin: bottom right;

    &.one {
      transform: rotate(30deg);
    }

    &.two {
      transform: rotate(60deg);
    }

    &.three {
      transform: rotate(90deg);
    }

    &.four {
      transform: rotate(120deg);
    }

    &.five {
      transform: rotate(150deg);
    }
  }

  &--min_line {
    position: absolute;
    z-index: 12;
    bottom: 0;
    left: $outsideBorder + 1px;
    right: 53%;
    height: 2px;
    background-color: $danger;
    transform-origin: bottom right;
  }

  &--tick {
    // rotation and color designated above
    position: absolute;
    z-index: 8;
    bottom: 0;
    left: 0;
    height: $tickThickness;
    width: 50%;
    border-radius: 6px;
    transform-origin: bottom right;
  }
}

.probability_line {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  padding-bottom: 8px;

  &:not(:last-of-type) {
    border-bottom: 1px solid $border;
  }
}

.probability_answer {
  font-weight: bold;
  color: $text-light;

  &.true {
    color: $success;
  }

  &.false {
    color: $danger;
  }
}
</style>
